'use client';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { ElementDialog } from '@/app/components/shared/ElementDialog';
import { ReferenceDetail } from '@/app/components/shared/ReferenceDetail';
import { Dialog } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { NewbieTaskName, TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { ErrorMsg, useCredits, useElementErrorMsg, useFetch, useFreeCreditsTasks, useMediaMetrics } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { CommunityEnum } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import { materialsGetElementDetail, materialsGetHistoryElementDetail } from './apis';
import type { ViduV1ElementDetail } from './types';

export const ReferenceModal = () => {
  const {
    referenceModalType,
    referenceModalOpen,
    referenceModalMode,
    element,
    updateReferenceModalType,
    updateReference,
    reference,
    referenceModalFrom,
    closeReferenceModal,
    userInfo,
    referenceModalFromPage,
    updateReferenceModalFrom,
  } = useStore((state) => ({
    referenceModalType: state.referenceModalType,
    referenceModalOpen: state.referenceModalOpen,
    referenceModalMode: state.referenceModalMode,
    element: state.element,
    updateReferenceModalType: state.updateReferenceModalType,
    updateReference: state.updateReference,
    reference: state.reference,
    closeReferenceModal: state.closeReferenceModal,
    updateReferenceModalFrom: state.updateReferenceModalFrom,
    userInfo: state.userInfo,
    referenceModalFrom: state.referenceModalFrom,
    referenceModalFromPage: state.referenceModalFromPage,
  }));

  const t = useTranslations();
  const { showErrorMsg } = useElementErrorMsg();
  const [init, setInit] = useState(false);
  const pathname = usePathname();
  const { handleEnter, handleLeave } = useMediaMetrics({
    callback: (detailStayTime: number) => {
      reference?.element?.id &&
        reportEvent(TRACE_EVENT_NAME[CommunityEnum.PostReferences], {
          [TRACE_EVENT_ID[CommunityEnum.PostReferences]]: reference?.element?.id || '',
          interaction_type: 'view_detail',
          interaction_position: pathname,
          detail_staytime: detailStayTime,
          content_type: 'video',
        });
    },
  });

  const { run: getReferenceDetail } = useFetch(
    () =>
      materialsGetElementDetail({
        path: { id: element?.id || '' },
      }),
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          if (referenceModalFrom === 'share') {
            closeReferenceModal();
            updateReferenceModalFrom('others');
            toast({
              type: 'error',
              message: t('create.shareFail'),
            });
            return;
          }
          showErrorMsg({
            ...data.error,
            reason: ErrorMsg.ElementForbidden,
          });
          closeReferenceModal({
            type: 'forbidden',
          });
        } else {
          const { element, share } = data.data;
          if (referenceModalFrom === 'share' && element?.share_meta?.status !== 'approved') {
            closeReferenceModal();
            updateReferenceModalFrom('others');
            toast({
              type: 'error',
              message: t('create.shareFail'),
            });
            return;
          }

          const isCreateShare = referenceModalType === 'share' && referenceModalMode === 'create';
          const isShare = ['approved', 'pending'].includes(element?.share_meta?.status ?? '');
          updateReferenceModalType(isCreateShare || isShare ? 'share' : 'reference');

          const reference: ViduV1ElementDetail = { element };
          if (share)
            reference.share = {
              ...share,
              // TODO 后续恢复此行代码
              // allow_copy: isCreateShare ? true : share?.allow_copy,
              allow_copy: isCreateShare ? false : share?.allow_copy,
            };
          updateReference(reference);
          if (isShare) {
            handleEnter();
            reportEvent('sharedreference_details_exposure', {
              from_page: referenceModalFromPage || '',
              status: element?.share_meta?.status === 'approved' ? 'success' : 'review',
              copy_yes: share?.allow_copy ? 1 : 0,
              reference_id: element?.id || '',
            });
          }
        }
      },
      onFinally: () => {
        setInit(true);
      },
    },
  );

  const { run: getHistoryReferenceDetail } = useFetch(
    () =>
      materialsGetHistoryElementDetail({
        query: {
          id: element?.id || '',
          version: element?.version || '',
        },
      }),
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg({
            ...data.error,
            reason: ErrorMsg.ElementForbidden,
          });
          closeReferenceModal({
            type: 'forbidden',
          });
        } else {
          const isShare = data.data.share?.id;
          updateReferenceModalType(isShare ? 'share' : 'reference');

          updateReference({
            element: data.data.element,
            share: data.data.share,
          });
          if (isShare) {
            handleEnter();
            reportEvent('sharedreference_details_exposure', {
              from_page: referenceModalFromPage || '',
              status: data.data.element?.share_meta?.status === 'approved' ? 'success' : 'review',
              copy_yes: data.data.share?.allow_copy ? 1 : 0,
              reference_id: data.data.element?.id || '',
            });
          }
        }
      },
      onFinally: () => {
        setInit(true);
      },
    },
  );

  useEffect(() => {
    if (referenceModalOpen) {
      setInit(false);
      if (element?.id) {
        if (element?.version && element.creator_id && userInfo?.id !== element.creator_id) {
          getHistoryReferenceDetail();
        } else {
          getReferenceDetail();
        }
      } else {
        updateReference({
          element: element || undefined,
        });
        setInit(true);
      }
    } else {
      setInit(false);
      handleLeave();
    }
  }, [referenceModalOpen, element, userInfo]);

  const { getExpectedNewbieReward, fetchFreeCreditTasks } = useFreeCreditsTasks();
  const { updateCredits } = useCredits();

  const onCreateSuccess = () => {
    const newbieReward = getExpectedNewbieReward(NewbieTaskName.newbie_task_material_create_credit);
    if (newbieReward > 0) {
      toast({
        type: 'success',
        message: t('newbieTask.completeToast', {
          points: newbieReward,
        }),
      });
      fetchFreeCreditTasks();
      updateCredits();
    }
  };

  if (!referenceModalOpen || !init) return null;

  return (
    <Dialog open={referenceModalOpen} onOpenChange={(isOpen: boolean) => !isOpen && closeReferenceModal()}>
      {/* 查看和投稿使用 ReferenceDetail 组件 */}
      {['view', 'post'].includes(referenceModalMode || '') ? (
        <ReferenceDetail onClose={closeReferenceModal} />
      ) : (
        // 创建和编辑使用 ElementDialog 组件
        <ElementDialog onCreateSuccess={onCreateSuccess} />
      )}
    </Dialog>
  );
};
